import React, { useEffect, useRef, useState } from 'react';
import { LibraryAsset } from './types';


/**
 * Library audio picker. Assets are shared and admin-curated, so a slide references the asset's
 * own R2 key rather than copying the file into the story.
 */
const AudioLibraryPicker: React.FC<{
  assets: LibraryAsset[];
  loading: boolean;
  onSelect: (asset: LibraryAsset) => void;
}> = ({ assets, loading, onSelect }) => {
  const [selectedId, setSelectedId] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const selected = assets.find(a => a.id === selectedId) || null;

  // One element per picker, so switching tracks or unmounting always stops playback.
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const togglePreview = () => {
    if (!selected?.previewUrl) return;
    if (playingId === selected.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const el = new Audio(selected.previewUrl);
    el.onended = () => setPlayingId(null);
    audioRef.current = el;
    el.play().then(() => setPlayingId(selected.id)).catch(() => setPlayingId(null));
  };

  const add = () => {
    if (!selected) return;
    audioRef.current?.pause();
    setPlayingId(null);
    onSelect(selected);
    setSelectedId('');
  };

  return (
    <div className="se-audio-picker">
      <select
        className="se-audio-picker-select"
        value={selectedId}
        onChange={e => { audioRef.current?.pause(); setPlayingId(null); setSelectedId(e.target.value); }}
        disabled={loading || assets.length === 0}
      >
        <option value="">
          {loading ? 'Loading library…' : assets.length === 0 ? 'No library audio' : 'Choose from library…'}
        </option>
        {assets.map(a => (
          <option key={a.id} value={a.id}>
            {a.name}{a.duration ? ` · ${Math.round(a.duration)}s` : ''}
          </option>
        ))}
      </select>
      <button
        className="se-audio-picker-btn"
        onClick={togglePreview}
        disabled={!selected?.previewUrl}
        title={playingId === selected?.id ? 'Stop preview' : 'Preview'}
      >
        {playingId === selected?.id ? '■' : '▶'}
      </button>
      <button className="se-audio-picker-btn se-audio-picker-add" onClick={add} disabled={!selected} title="Add to slide">
        Add
      </button>
    </div>
  );
};

export default AudioLibraryPicker;

